import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Dropdown } from "react-bootstrap";
import { saveAs } from "file-saver";
import { loading, alert } from "../CustomComponent";
import pdfjsLib from "./pdfjs";
import { exportPdf } from "./pdfExport";
import { loadEditorFonts } from "./fonts";
import { normRot, placeAtVisible, totalRotation, visibleSize } from "./geometry";
import { loadImage, prepareImage, readAsBytes } from "./imageUtils";
import PageView, { DEFAULT_TEXT } from "./PageView";
import PageThumb from "./PageThumb";
import AssetLibraryModal from "./AssetLibraryModal";
import { formatToday, waitForSwalClose } from "./textUtils";
import { driveSource } from "./sources";
import { copyObjects, hasClipboard, pasteObjects } from "./objectClipboard";

const PRIMARY = "#0c76e5";
const INK = "#22384a";
const MAX_HISTORY = 60;
const MIN_ZOOM = 0.3;
const MAX_ZOOM = 4;

const TOOL_DEFAULTS = {
  pen: { color: "#111827", width: 2 },
  highlight: { color: "#ffe600", width: 14 },
  rect: { color: "#dc2626", width: 2 },
  text: { color: "#111827" },
};

let idSeed = 0;
const nextId = (prefix) => {
  idSeed += 1;
  return `${prefix}${Date.now().toString(36)}${idSeed}`;
};

const EMPTY_IDS = [];
const PDF_OBJECT_TYPES = ["text", "image", "rect", "stroke", "highlight"];

function ToolButton({ icon, label, active, onClick, disabled, title }) {
  return (
    <button
      type="button"
      className="btn btn-sm d-flex flex-column align-items-center justify-content-center px-2 py-1 border-0"
      style={{
        minWidth: 56, minHeight: 48, borderRadius: 10,
        background: active ? "rgba(12,118,229,0.14)" : "transparent",
        color: active ? PRIMARY : INK, fontWeight: active ? 600 : 400,
      }}
      onClick={onClick}
      disabled={disabled}
      title={title || label}
    >
      <i className={`fa-solid ${icon}`} style={{ fontSize: 16 }} />
      <span style={{ fontSize: 11, marginTop: 2, whiteSpace: "nowrap" }}>{label}</span>
    </button>
  );
}

// file: { uid, name, folder_uid } จาก CompanyDrive — onSaved ใช้รีเฟรช getData/getUsage
// source: adapter แหล่งไฟล์/วิธีบันทึก (ดู sources.js) ถ้าไม่ส่งมาจะใช้ไฟล์ของ drive จาก file
export default function PdfEditor({ file, source: sourceProp, onClose, onSaved }) {
  const sourceRef = useRef(null);
  if (!sourceRef.current) sourceRef.current = sourceProp || driveSource(file);
  const source = sourceRef.current;
  const editorId = useRef(nextId("e")).current;
  const [, setClipTick] = useState(0);
  const [ready, setReady] = useState(false);
  const [pages, setPages] = useState([]);
  const [historyLen, setHistoryLen] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [tool, setTool] = useState("select");
  const [style, setStyle] = useState({ color: "#111827", width: 2, textSize: 16 });
  const [selected, setSelected] = useState(null); // { pageId, ids: [objId, ...] }
  const [activePageId, setActivePageId] = useState(null);
  const [assetType, setAssetType] = useState(null);
  const [dragFrom, setDragFrom] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [, setSavedTick] = useState(0); // บังคับ render ให้ป้าย "ยังไม่บันทึก" หายหลังบันทึก

  const pagesRef = useRef([]);
  const savedRef = useRef(null); // pages ณ จุดที่บันทึกล่าสุด/โหลดมา -> เทียบหา dirty
  const historyRef = useRef([]);
  const lastEditKey = useRef(null);
  const docsRef = useRef({}); // docId -> { bytes, pdf }
  const scrollRef = useRef(null);
  const imageInputRef = useRef(null);
  const mergeInputRef = useRef(null);
  const textAreaRef = useRef(null);
  const closedRef = useRef(false);

  const dirty = pages !== savedRef.current;

  /* ---------- state + undo ---------- */

  const pushHistory = useCallback(() => {
    historyRef.current.push(pagesRef.current);
    if (historyRef.current.length > MAX_HISTORY) historyRef.current.shift();
    lastEditKey.current = null;
    setHistoryLen(historyRef.current.length);
  }, []);

  const commit = useCallback((next, record = true) => {
    if (record) pushHistory();
    pagesRef.current = next;
    setPages(next);
  }, [pushHistory]);

  const undo = useCallback(() => {
    const prev = historyRef.current.pop();
    if (!prev) return;
    lastEditKey.current = null;
    setHistoryLen(historyRef.current.length);
    pagesRef.current = prev;
    setPages(prev);
    setSelected(null);
  }, []);

  const mapPage = (pageId, fn) => pagesRef.current.map((p) => (p.id === pageId ? fn(p) : p));

  const addObject = useCallback((pageId, obj) => {
    const withId = { ...obj, id: nextId("o") };
    commit(mapPage(pageId, (p) => ({ ...p, objects: [...p.objects, withId] })));
    setSelected({ pageId, ids: [withId.id] });
    if (obj.type === "text" || obj.type === "image") setTool("select");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commit]);

  const updateObject = useCallback((pageId, objId, patch) => {
    commit(mapPage(pageId, (p) => ({ ...p, objects: p.objects.map((o) => (o.id === objId ? { ...o, ...patch } : o)) })), false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commit]);

  const updateMany = useCallback((pageId, patches) => {
    commit(mapPage(pageId, (p) => ({ ...p, objects: p.objects.map((o) => (patches[o.id] ? { ...o, ...patches[o.id] } : o)) })), false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commit]);

  const deleteSelected = useCallback(() => {
    if (!selected?.ids?.length) return;
    commit(mapPage(selected.pageId, (p) => ({ ...p, objects: p.objects.filter((o) => !selected.ids.includes(o.id)) })));
    setSelected(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commit, selected]);

  const selectedObj = useMemo(() => {
    if (selected?.ids?.length !== 1) return null;
    const page = pages.find((p) => p.id === selected.pageId);
    return page?.objects.find((o) => o.id === selected.ids[0]) || null;
  }, [pages, selected]);

  /* ---------- โหลดเอกสาร ---------- */

  const readPages = useCallback(async (pdf, docId) => {
    const out = [];
    for (let i = 1; i <= pdf.numPages; i += 1) {
      const p = await pdf.getPage(i);
      const vp = p.getViewport({ scale: 1, rotation: 0 });
      out.push({
        id: nextId("p"), docId, srcIndex: i - 1, baseRotation: normRot(p.rotate || 0),
        userRotation: 0, W: vp.width, H: vp.height, objects: [],
      });
    }
    return out;
  }, []);

  const openBytes = useCallback(async (bytes) => {
    const docId = nextId("d");
    // pdfjs ดึง buffer ไปใช้ใน worker -> ส่งสำเนา เก็บต้นฉบับไว้ให้ pdf-lib
    const task = pdfjsLib.getDocument({ data: bytes.slice() });
    const pdf = await task.promise;
    docsRef.current[docId] = { bytes, pdf, task };
    return readPages(pdf, docId);
  }, [readPages]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      loading(true);
      try {
        const [loaded] = await Promise.all([source.load(), loadEditorFonts()]);
        if (cancelled) return;
        if (!loaded?.bytes) {
          loading(false); // ปิด overlay ก่อน alert ไม่งั้นบังปุ่ม SweetAlert
          await alert("", "warning", "แจ้งเตือน", loaded?.error || "เปิดไฟล์ไม่สำเร็จ");
          onClose();
          return;
        }
        let first = await openBytes(loaded.bytes);
        if (cancelled) return;
        if (!first.length) throw new Error("ไฟล์ PDF ไม่มีหน้า");
        // adapter ที่เก็บ layout ไว้ (เช่นสัญญา) คืนลำดับหน้า/การหมุน/ออบเจกต์เดิมกลับมา
        if (loaded.restore) {
          first = await loaded.restore(first, nextId);
          if (cancelled) return;
        }

        savedRef.current = first;
        pagesRef.current = first;
        setPages(first);
        setActivePageId(first[0].id);
        setReady(true);

        // ซูมให้พอดีความกว้างหน้าแรก
        requestAnimationFrame(() => {
          const area = scrollRef.current;
          if (!area) return;
          const v = visibleSize(first[0].W, first[0].H, totalRotation(first[0]));
          const fit = Math.round(((area.clientWidth - 64) / v.w) * 20) / 20;
          setZoom(Math.min(2, Math.max(0.5, fit)));
        });
      } catch (error) {
        if (!cancelled) {
          loading(false);
          await alert("", "warning", "แจ้งเตือน", error?.message || "เปิดไฟล์ PDF ไม่สำเร็จ");
          onClose();
        }
      } finally {
        loading(false);
      }
    })();

    return () => {
      cancelled = true;
      // pdfjs-dist 6.x ถอด PDFDocumentProxy.destroy() ต้องปิดผ่าน loadingTask
      Object.values(docsRef.current).forEach((d) => d.task?.destroy?.());
      docsRef.current = {};
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source.uid]);

  /* ---------- ปิด / เตือนก่อนออก ---------- */

  const requestClose = useCallback(async () => {
    if (isSaving || closedRef.current) return;
    if (pagesRef.current !== savedRef.current) {
      const ok = await alert("", "warning", "ยังไม่ได้บันทึก", "มีการแก้ไขที่ยังไม่ได้บันทึก ต้องการปิดโดยไม่บันทึกหรือไม่?", "ปิดโดยไม่บันทึก", "กลับไปแก้ไข");
      if (!ok) return;
    }
    closedRef.current = true;
    onClose();
  }, [isSaving, onClose]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const pageInfo = (page) => ({ W: page.W, H: page.H, R: totalRotation(page) });

  const copySelected = useCallback(() => {
    if (!selected?.ids?.length) return;
    const page = pagesRef.current.find((p) => p.id === selected.pageId);
    if (!page) return;
    const objs = page.objects.filter((o) => selected.ids.includes(o.id));
    if (copyObjects(objs, pageInfo(page), `${editorId}:${page.id}`)) setClipTick((t) => t + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, editorId]);

  // วางลงหน้าที่กำลังดู ตำแหน่งสัมพัทธ์เดียวกับต้นทาง (คิดตามที่เห็น) แล้วเลือกของที่วางไว้ต่อทันที
  const pasteClip = useCallback(() => {
    const page = pagesRef.current.find((p) => p.id === activePageId) || pagesRef.current[0];
    if (!page) return;
    const objs = pasteObjects(pageInfo(page), `${editorId}:${page.id}`, { accepts: PDF_OBJECT_TYPES, newId: () => nextId("o") });
    if (!objs.length) return;
    commit(mapPage(page.id, (p) => ({ ...p, objects: [...p.objects, ...objs] })));
    setSelected({ pageId: page.id, ids: objs.map((o) => o.id) });
    setTool("select");
    setClipTick((t) => t + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePageId, commit, editorId]);

  const selectAllOnPage = useCallback(() => {
    const page = pagesRef.current.find((p) => p.id === activePageId) || pagesRef.current[0];
    if (page?.objects.length) setSelected({ pageId: page.id, ids: page.objects.map((o) => o.id) });
  }, [activePageId]);

  useEffect(() => {
    const onKeyDown = (event) => {
      const tag = event.target?.tagName;
      const typing = tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || event.target?.isContentEditable;
      const mod = event.metaKey || event.ctrlKey;
      const key = event.key.toLowerCase();
      if (typing || document.querySelector(".swal2-container")) {
        if (event.key === "Escape" && !typing && !document.querySelector(".swal2-container")) setSelected(null);
        return;
      }
      if (mod && key === "z") {
        event.preventDefault();
        undo();
      } else if (mod && key === "c" && selected?.ids?.length) {
        event.preventDefault();
        copySelected();
      } else if (mod && key === "v" && hasClipboard()) {
        event.preventDefault();
        pasteClip();
      } else if (mod && key === "a") {
        event.preventDefault();
        selectAllOnPage();
      } else if ((event.key === "Delete" || event.key === "Backspace") && selected?.ids?.length) {
        event.preventDefault();
        deleteSelected();
      } else if (event.key === "Escape") {
        setSelected(null);
        setTool("select");
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [undo, deleteSelected, selected, copySelected, pasteClip, selectAllOnPage]);

  // ข้อความที่เพิ่งวางใหม่ -> โฟกัสช่องพิมพ์ทันที
  useEffect(() => {
    if (selectedObj?.type === "text" && selectedObj.text === DEFAULT_TEXT && textAreaRef.current) {
      textAreaRef.current.focus();
      textAreaRef.current.select();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.ids?.[0], selected?.ids?.length]);

  /* ---------- เครื่องมือ / สไตล์ ---------- */

  const chooseTool = (next) => {
    setTool(next);
    setSelected(null);
    if (TOOL_DEFAULTS[next]) setStyle((s) => ({ ...s, ...TOOL_DEFAULTS[next] }));
  };

  // แก้สไตล์ของออบเจกต์ที่เลือก (ต่อเนื่องจาก slider/สี รวมเป็น 1 ขั้น undo)
  const editSelected = (key, patch) => {
    if (!selectedObj) return;
    const editKey = `${selectedObj.id}:${key}`;
    if (lastEditKey.current !== editKey) {
      pushHistory();
      lastEditKey.current = editKey;
    }
    updateObject(selected.pageId, selected.ids[0], patch);
  };

  const changeColor = (color) => {
    setStyle((s) => ({ ...s, color }));
    if (selectedObj && selectedObj.color !== undefined) editSelected("color", { color });
  };
  const changeWidth = (width) => {
    setStyle((s) => ({ ...s, width }));
    if (selectedObj && ["stroke", "highlight", "rect"].includes(selectedObj.type)) editSelected("width", { width });
  };
  const changeTextSize = (size) => {
    const value = Math.min(200, Math.max(4, Number(size) || 4));
    setStyle((s) => ({ ...s, textSize: value }));
    if (selectedObj?.type === "text") editSelected("size", { size: value });
  };

  /* ---------- เพิ่มรูป / ลายเซ็น / ตราปั๊ม / ข้อความ ---------- */

  const activePage = () => pagesRef.current.find((p) => p.id === activePageId) || pagesRef.current[0];

  const addImageFromDataUrl = async (dataUrl, defaultWidth) => {
    const page = activePage();
    if (!page) return;
    const img = await loadImage(dataUrl);
    const R = totalRotation(page);
    const v = visibleSize(page.W, page.H, R);
    const w = Math.min(defaultWidth, v.w * 0.6);
    const h = (w * img.height) / img.width;
    addObject(page.id, { type: "image", ...placeAtVisible((v.w - w) / 2, (v.h - h) / 2, page.W, page.H, R), w, h, src: dataUrl });
  };

  const handlePickAsset = async (dataUrl) => {
    const type = assetType;
    setAssetType(null);
    try {
      await addImageFromDataUrl(dataUrl, type === "stamp" ? 130 : 170);
    } catch (error) {
      await alert("", "warning", "แจ้งเตือน", error.message || "แทรกรูปไม่สำเร็จ");
    }
  };

  const handleImageFile = async (event) => {
    const picked = event.target.files?.[0];
    event.target.value = "";
    if (!picked) return;
    if (!picked.type.startsWith("image/")) {
      await alert("", "warning", "แจ้งเตือน", "กรุณาเลือกไฟล์รูปภาพ");
      return;
    }
    try {
      const prepared = await prepareImage(picked);
      await addImageFromDataUrl(prepared.dataUrl, 240);
    } catch (error) {
      await alert("", "warning", "แจ้งเตือน", error.message || "แทรกรูปไม่สำเร็จ");
    }
  };

  const addDate = (era) => {
    const page = activePage();
    if (!page) return;
    const R = totalRotation(page);
    const v = visibleSize(page.W, page.H, R);
    addObject(page.id, {
      type: "text", ...placeAtVisible(v.w * 0.35, v.h * 0.45, page.W, page.H, R),
      text: formatToday(era), size: style.textSize, color: style.color, bold: false,
    });
  };

  /* ---------- จัดการหน้า ---------- */

  const scrollToPage = (pageId) => {
    const node = scrollRef.current?.querySelector(`[data-page-id="${pageId}"]`);
    if (node && scrollRef.current) scrollRef.current.scrollTo({ top: node.offsetTop - 16, behavior: "smooth" });
  };

  const handleScroll = () => {
    const area = scrollRef.current;
    if (!area) return;
    const mid = area.scrollTop + area.clientHeight / 2;
    let best = null;
    let bestDist = Infinity;
    area.querySelectorAll("[data-page-id]").forEach((node) => {
      const dist = Math.abs(node.offsetTop + node.offsetHeight / 2 - mid);
      if (dist < bestDist) {
        bestDist = dist;
        best = node.getAttribute("data-page-id");
      }
    });
    if (best && best !== activePageId) setActivePageId(best);
  };

  const rotatePage = (delta) => {
    const page = activePage();
    if (!page) return;
    commit(mapPage(page.id, (p) => ({ ...p, userRotation: normRot(p.userRotation + delta) })));
  };

  const deletePage = () => {
    const page = activePage();
    if (!page || pagesRef.current.length <= 1) return;
    const index = pagesRef.current.findIndex((p) => p.id === page.id);
    const next = pagesRef.current.filter((p) => p.id !== page.id);
    commit(next);
    setSelected(null);
    setActivePageId(next[Math.min(index, next.length - 1)].id);
  };

  const movePage = (from, to) => {
    const list = pagesRef.current;
    if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) return;
    const next = list.slice();
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    commit(next);
  };

  const handleMergeFile = async (event) => {
    const picked = event.target.files?.[0];
    event.target.value = "";
    if (!picked) return;
    loading(true);
    try {
      const added = await openBytes(await readAsBytes(picked));
      commit([...pagesRef.current, ...added]);
      setActivePageId(added[0].id);
      setTimeout(() => scrollToPage(added[0].id), 50);
    } catch (error) {
      loading(false);
      await alert("", "warning", "แจ้งเตือน", "รวมไฟล์ไม่สำเร็จ (ไฟล์อาจไม่ใช่ PDF หรือมีรหัสผ่าน)");
    } finally {
      loading(false);
    }
  };

  /* ---------- บันทึก ---------- */

  const buildPdf = async () => {
    const fonts = await loadEditorFonts();
    const sources = {};
    Object.keys(docsRef.current).forEach((id) => { sources[id] = docsRef.current[id].bytes; });
    return exportPdf({ sources, pages: pagesRef.current, fonts });
  };

  const finishSave = async (message) => {
    savedRef.current = pagesRef.current;
    setSavedTick((t) => t + 1);
    onSaved?.();
    await waitForSwalClose();
    await alert("", "success", "สำเร็จ", message);
    if (source.closeAfterSave && !closedRef.current) {
      closedRef.current = true;
      onClose();
    }
  };

  const handleSave = async (mode) => {
    if (isSaving) return;

    const option = source.saveOptions.find((o) => o.mode === mode);
    if (option?.confirm) {
      const ok = await alert("", option.confirm.icon || "warning", option.confirm.title, option.confirm.text, option.confirm.ok, "ยกเลิก");
      if (!ok) return;
    }

    setIsSaving(true);
    loading(true);
    try {
      const bytes = await buildPdf();

      if (mode === "download") {
        saveAs(new Blob([bytes], { type: "application/pdf" }), source.downloadName);
        return;
      }

      // adapter ของแต่ละแหล่งไฟล์ตัดสินว่าส่งไป API ไหน (drive: ไฟล์ใหม่/ทับ, สัญญา: ฉบับประทับตรา + layout)
      const result = await source.save(mode, { bytes, pages: pagesRef.current });
      loading(false);

      if (result?.status) {
        await finishSave(result.doneMessage || "บันทึกเรียบร้อย");
      } else {
        await alert("", "warning", "แจ้งเตือน", result?.message || "บันทึกไม่สำเร็จ");
      }
    } catch (error) {
      loading(false);
      await alert("", "warning", "แจ้งเตือน", error?.message || "สร้างไฟล์ PDF ไม่สำเร็จ");
    } finally {
      loading(false);
      setIsSaving(false);
    }
  };

  /* ---------- ส่วนแสดงผล ---------- */

  const stepZoom = (factor) => setZoom((z) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, Math.round(z * factor * 100) / 100)));

  const showWidth = ["pen", "highlight", "rect"].includes(tool) || ["stroke", "highlight", "rect"].includes(selectedObj?.type);
  const showTextSize = tool === "text" || selectedObj?.type === "text";
  const showColor = tool !== "select" || (selectedObj && selectedObj.color !== undefined);
  const activeIndex = pages.findIndex((p) => p.id === activePageId);

  const toolbarDivider = <div style={{ width: 1, alignSelf: "stretch", background: "rgba(18,125,255,0.15)", margin: "4px 6px" }} />;

  const content = (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 1050, background: "#eef3f9", display: "flex", flexDirection: "column", color: INK }}
      role="dialog"
      aria-label="แก้ไข PDF"
    >
      {/* แถบบน */}
      <div className="d-flex align-items-center gap-2 px-3 py-2 bg-white flex-wrap" style={{ borderBottom: "1px solid rgba(18,125,255,0.12)" }}>
        <div className="d-flex align-items-center gap-2 me-auto" style={{ minWidth: 0 }}>
          <i className="fa-solid fa-file-pdf" style={{ color: "#dc2626", fontSize: 20 }} />
          <div className="fw-semibold text-truncate" style={{ maxWidth: 360 }}>{source.name}</div>
          {dirty ? <span className="badge" style={{ background: "rgba(226,113,29,0.15)", color: "#e2711d" }}>ยังไม่บันทึก</span> : null}
        </div>

        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={undo} disabled={!historyLen} title="ย้อนกลับ (Ctrl/Cmd+Z)">
          <i className="fa-solid fa-rotate-left me-1" />ย้อนกลับ
        </button>

        <div className="btn-group btn-group-sm" role="group" aria-label="ซูม">
          <button type="button" className="btn btn-outline-secondary" onClick={() => stepZoom(1 / 1.2)} title="ซูมออก"><i className="fa-solid fa-magnifying-glass-minus" /></button>
          <button type="button" className="btn btn-outline-secondary" style={{ minWidth: 64 }} disabled>{Math.round(zoom * 100)}%</button>
          <button type="button" className="btn btn-outline-secondary" onClick={() => stepZoom(1.2)} title="ซูมเข้า"><i className="fa-solid fa-magnifying-glass-plus" /></button>
        </div>

        <Dropdown align="end">
          <Dropdown.Toggle size="sm" variant="primary" disabled={!ready || isSaving}>
            <i className="fa-solid fa-floppy-disk me-1" />บันทึก
          </Dropdown.Toggle>
          <Dropdown.Menu style={{ zIndex: 1100 }}>
            {source.saveOptions.map((o) => (
              <Dropdown.Item key={o.mode} onClick={() => handleSave(o.mode)}><i className={`fa-solid ${o.icon} me-2`} />{o.label}</Dropdown.Item>
            ))}
            <Dropdown.Divider />
            <Dropdown.Item onClick={() => handleSave("download")}><i className="fa-solid fa-download me-2" />ดาวน์โหลดลงเครื่อง</Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>

        <button type="button" className="btn btn-outline-secondary btn-sm" onClick={requestClose} title="ปิด">
          <i className="fa-solid fa-xmark me-1" />ปิด
        </button>
      </div>

      {/* แถบเครื่องมือ */}
      <div className="d-flex align-items-center flex-wrap px-3 py-1 bg-white" style={{ borderBottom: "1px solid rgba(18,125,255,0.12)" }}>
        <ToolButton icon="fa-arrow-pointer" label="เลือก" active={tool === "select"} onClick={() => chooseTool("select")} />
        {toolbarDivider}
        <ToolButton icon="fa-signature" label="ลายเซ็น" onClick={() => setAssetType("signature")} disabled={!ready} />
        <ToolButton icon="fa-stamp" label="ตราปั๊ม" onClick={() => setAssetType("stamp")} disabled={!ready} />
        <ToolButton icon="fa-image" label="รูปภาพ" onClick={() => imageInputRef.current?.click()} disabled={!ready} />
        {toolbarDivider}
        <ToolButton icon="fa-font" label="ข้อความ" active={tool === "text"} onClick={() => chooseTool("text")} disabled={!ready} />
        <Dropdown>
          <Dropdown.Toggle as="button" type="button" className="btn btn-sm d-flex flex-column align-items-center justify-content-center px-2 py-1 border-0" disabled={!ready}
            style={{ minWidth: 56, minHeight: 48, borderRadius: 10, background: "transparent", color: INK }}>
            <i className="fa-solid fa-calendar-day" style={{ fontSize: 16 }} />
            <span style={{ fontSize: 11, marginTop: 2 }}>วันที่</span>
          </Dropdown.Toggle>
          <Dropdown.Menu style={{ zIndex: 1100 }}>
            <Dropdown.Item onClick={() => addDate("be")}>{formatToday("be")} (พ.ศ.)</Dropdown.Item>
            <Dropdown.Item onClick={() => addDate("ce")}>{formatToday("ce")} (ค.ศ.)</Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
        {toolbarDivider}
        <ToolButton icon="fa-pen" label="ปากกา" active={tool === "pen"} onClick={() => chooseTool("pen")} disabled={!ready} />
        <ToolButton icon="fa-highlighter" label="ไฮไลต์" active={tool === "highlight"} onClick={() => chooseTool("highlight")} disabled={!ready} />
        <ToolButton icon="fa-square" label="กล่อง" active={tool === "rect"} onClick={() => chooseTool("rect")} disabled={!ready} />
        {toolbarDivider}
        <ToolButton icon="fa-copy" label="คัดลอก" onClick={copySelected} disabled={!selected?.ids?.length} title="คัดลอกที่เลือก (Ctrl/Cmd+C)" />
        <ToolButton icon="fa-paste" label="วาง" onClick={pasteClip} disabled={!ready || !hasClipboard()} title="วางลงหน้าที่กำลังดู (Ctrl/Cmd+V)" />

        {/* ตัวเลือกตามเครื่องมือ/ออบเจกต์ที่เลือก */}
        <div className="d-flex align-items-center flex-wrap gap-3 ms-3">
          {showColor ? (
            <label className="d-flex align-items-center gap-2 small mb-0">
              สี
              <input type="color" value={selectedObj?.color || style.color} onChange={(e) => changeColor(e.target.value)} style={{ width: 34, height: 28, padding: 0, border: "1px solid #cbd5e1", borderRadius: 6 }} />
            </label>
          ) : null}
          {showWidth ? (
            <label className="d-flex align-items-center gap-2 small mb-0">
              ความหนา
              <input
                type="range" min="1" max={(tool === "highlight" || selectedObj?.type === "highlight") ? 40 : 20}
                value={selectedObj?.width || style.width} onChange={(e) => changeWidth(Number(e.target.value))}
              />
              <span style={{ minWidth: 22 }}>{selectedObj?.width || style.width}</span>
            </label>
          ) : null}
          {showTextSize ? (
            <label className="d-flex align-items-center gap-2 small mb-0">
              ขนาด
              <input type="number" className="form-control form-control-sm" style={{ width: 70 }} min="4" max="200"
                value={selectedObj?.type === "text" ? selectedObj.size : style.textSize} onChange={(e) => changeTextSize(e.target.value)} />
            </label>
          ) : null}
          {selectedObj?.type === "text" ? (
            <>
              <textarea
                ref={textAreaRef}
                className="form-control form-control-sm"
                rows={1}
                style={{ width: 240, resize: "vertical" }}
                value={selectedObj.text}
                placeholder="พิมพ์ข้อความ"
                onChange={(e) => editSelected("text", { text: e.target.value })}
              />
              <label className="d-flex align-items-center gap-1 small mb-0">
                <input type="checkbox" checked={!!selectedObj.bold} onChange={(e) => editSelected("bold", { bold: e.target.checked })} />ตัวหนา
              </label>
            </>
          ) : null}
          {selectedObj?.type === "image" ? (
            <label className="d-flex align-items-center gap-2 small mb-0">
              ความโปร่งใส
              <input type="range" min="10" max="100" value={Math.round((selectedObj.opacity ?? 1) * 100)} onChange={(e) => editSelected("opacity", { opacity: Number(e.target.value) / 100 })} />
            </label>
          ) : null}
          {selected?.ids?.length > 1 ? <span className="small text-secondary">เลือก {selected.ids.length} ชิ้น</span> : null}
          {selected?.ids?.length ? (
            <button type="button" className="btn btn-outline-danger btn-sm" onClick={deleteSelected} title="ลบที่เลือก (Delete)">
              <i className="fa-solid fa-trash me-1" />ลบ
            </button>
          ) : null}
        </div>
      </div>

      {/* เนื้อหา */}
      <div className="d-flex flex-grow-1" style={{ minHeight: 0 }}>
        <div className="bg-white d-flex flex-column flex-shrink-0" style={{ width: 168, borderRight: "1px solid rgba(18,125,255,0.12)" }}>
          <div className="p-2" style={{ borderBottom: "1px solid rgba(18,125,255,0.10)" }}>
            <div className="small fw-semibold mb-2">หน้า {activeIndex + 1 || "-"} / {pages.length}</div>
            <div className="d-flex flex-wrap gap-1">
              <button type="button" className="btn btn-outline-secondary btn-sm" title="หมุนซ้าย 90°" onClick={() => rotatePage(-90)}><i className="fa-solid fa-rotate-left" /></button>
              <button type="button" className="btn btn-outline-secondary btn-sm" title="หมุนขวา 90°" onClick={() => rotatePage(90)}><i className="fa-solid fa-rotate-right" /></button>
              <button type="button" className="btn btn-outline-secondary btn-sm" title="เลื่อนหน้าขึ้น" onClick={() => movePage(activeIndex, activeIndex - 1)} disabled={activeIndex <= 0}><i className="fa-solid fa-arrow-up" /></button>
              <button type="button" className="btn btn-outline-secondary btn-sm" title="เลื่อนหน้าลง" onClick={() => movePage(activeIndex, activeIndex + 1)} disabled={activeIndex < 0 || activeIndex >= pages.length - 1}><i className="fa-solid fa-arrow-down" /></button>
              <button type="button" className="btn btn-outline-danger btn-sm" title="ลบหน้านี้ (ต้องเหลืออย่างน้อย 1 หน้า)" onClick={deletePage} disabled={pages.length <= 1}><i className="fa-solid fa-trash" /></button>
            </div>
            {source.allowMerge ? (
              <button type="button" className="btn btn-outline-primary btn-sm w-100 mt-2" onClick={() => mergeInputRef.current?.click()} disabled={!ready}>
                <i className="fa-solid fa-file-circle-plus me-1" />รวม PDF
              </button>
            ) : null}
          </div>
          <div className="flex-grow-1 p-2" style={{ overflowY: "auto" }}>
            {pages.map((page, index) => (
              <PageThumb
                key={page.id}
                page={page}
                pdfDoc={docsRef.current[page.docId]?.pdf}
                index={index}
                active={page.id === activePageId}
                dragOver={dragOver === index && dragFrom !== null && dragFrom !== index}
                onClick={() => { setActivePageId(page.id); scrollToPage(page.id); }}
                onDragStart={(e) => { setDragFrom(index); e.dataTransfer.effectAllowed = "move"; }}
                onDragOver={(e) => { if (dragFrom === null) return; e.preventDefault(); setDragOver(index); }}
                onDrop={(e) => { e.preventDefault(); if (dragFrom !== null) movePage(dragFrom, index); setDragFrom(null); setDragOver(null); }}
                onDragEnd={() => { setDragFrom(null); setDragOver(null); }}
              />
            ))}
          </div>
        </div>

        <div ref={scrollRef} className="flex-grow-1" style={{ overflow: "auto", padding: "24px 32px" }} onScroll={handleScroll}>
          <div style={{ width: "max-content", minWidth: "100%", margin: "0 auto" }} className="d-flex flex-column align-items-center">
            {pages.map((page, index) => (
              <PageView
                key={page.id}
                page={page}
                pdfDoc={docsRef.current[page.docId]?.pdf}
                index={index}
                zoom={zoom}
                tool={tool}
                color={style.color}
                width={style.width}
                textSize={style.textSize}
                active={page.id === activePageId}
                selectedIds={selected?.pageId === page.id ? selected.ids : EMPTY_IDS}
                onActivate={setActivePageId}
                onSelect={(ids) => setSelected(ids.length ? { pageId: page.id, ids } : null)}
                onBeginEdit={pushHistory}
                onUpdateMany={updateMany}
                onAdd={addObject}
              />
            ))}
          </div>
        </div>
      </div>

      <input ref={imageInputRef} type="file" accept="image/*" hidden onChange={handleImageFile} />
      <input ref={mergeInputRef} type="file" accept="application/pdf,.pdf" hidden onChange={handleMergeFile} />

      <AssetLibraryModal type={assetType || "signature"} show={!!assetType} onHide={() => setAssetType(null)} onPick={handlePickAsset} />
    </div>
  );

  return createPortal(content, document.body);
}
